<template>
    <main class="subscription-page">
        <header class="subscription-topbar">
            <button class="brand" type="button" @click="goToAccount">
                <img src="https://swiftinvoicewebsite.s3-us-west-2.amazonaws.com/tab_logo.ico" alt="">
                <span>Swift Invoice</span>
            </button>
            <button class="close-button" type="button" aria-label="Back to account" @click="goToAccount">
                <i class="el-icon-close" aria-hidden="true"></i>
            </button>
        </header>

        <section class="subscription-intro">
            <h1>Choose a plan</h1>
        </section>

        <section class="plan-comparison" aria-label="Swift Invoice plans">
            <article
                v-for="plan in plans"
                :key="plan.name"
                class="plan-lane"
                :class="{
                    'is-current': currentPlan === plan.name,
                    'is-highlighted': selectedPlan === plan.name && currentPlan !== plan.name
                }"
            >
                <header class="plan-lane-heading">
                    <div class="plan-lane-meta">
                        <span v-if="currentPlan === plan.name" class="status-label">Current plan</span>
                        <span v-else-if="plan.discountLabel" class="saving-label">{{plan.discountLabel}}</span>
                    </div>
                    <h2>{{plan.title}}</h2>
                    <div class="plan-lane-price">
                        <span v-if="plan.originalPrice" class="original-price">{{plan.originalPrice}}</span>
                        <span v-else class="original-price is-empty" aria-hidden="true">$0</span>
                        <div class="current-price">
                            <strong>{{plan.price}}</strong>
                            <span>{{plan.priceNote}}</span>
                        </div>
                    </div>
                </header>

                <ul class="plan-feature-list">
                    <li v-for="feature in plan.features" :key="feature">
                        <i class="el-icon-check" aria-hidden="true"></i>
                        <span>{{feature}}</span>
                    </li>
                </ul>

                <footer class="plan-lane-actions">
                    <button
                        v-if="plan.name === 'Free'"
                        class="plan-button secondary-button"
                        type="button"
                        @click="goToInvoices"
                    >
                        Continue with Free
                    </button>
                    <button
                        v-else-if="canSubscribe(plan.name)"
                        class="plan-button"
                        type="button"
                        @click="openCheckout(plan)"
                    >
                        Subscribe
                    </button>
                </footer>
            </article>
        </section>

    <transition name="checkout-modal">
      <div
        v-show="checkoutPlan"
                class="checkout-overlay"
                role="presentation"
                @click.self="closeCheckout"
            >
                <section
                    class="checkout-dialog"
                    role="dialog"
                    aria-modal="true"
                    :aria-label="checkoutPlan ? checkoutPlan.title + ' secure checkout' : 'Secure checkout'"
                >
                    <header class="checkout-heading">
                        <div>
                            <div class="eyebrow">Secure checkout</div>
                            <h2>{{checkoutPlan ? checkoutPlan.title : ''}}</h2>
                        </div>
                        <button class="close-checkout" type="button" aria-label="Close checkout" @click="closeCheckout">
                            <i class="el-icon-close" aria-hidden="true"></i>
                        </button>
                    </header>
                    <div class="checkout-body" aria-live="polite">
                        <div v-if="checkoutError" class="checkout-error" role="alert">{{checkoutError}}</div>
                        <div class="checkout-frame" :class="{'is-hidden': checkoutError}">
                            <div v-if="checkoutLoading" class="checkout-loading" role="status" aria-label="Loading secure checkout">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                            <div id="fsc-embedded-checkout-container"></div>
                        </div>
                    </div>
                </section>
            </div>
        </transition>

        <p class="support-note">
            You can cancel your subscription at any time by email.
            <a href="mailto:goodinvoicego@gmail.com?subject=Web%20Subscription%20Issue">Contact support</a>
        </p>
    </main>
</template>

<script>
import {getFastSpringPageContext} from '@/utils/subscription'
import {reportHandledError} from '@/utils/sentry'

const FASTSPRING_SCRIPT_ID = 'fsc-api';
const FASTSPRING_SCRIPT_URL = 'https://sbl.onfastspring.com/sbl/1.0.7/fastspring-builder.min.js';
const FASTSPRING_STOREFRONT = process.env.NODE_ENV === 'production'
    ? 'swiftinvoice.onfastspring.com/embedded-subscription'
    : 'swiftinvoice.test.onfastspring.com/embedded-subscription';
const FASTSPRING_PRO_PRODUCT_PATH = 'monthlypro-test';

export default {
    name: 'Subscription',
    data() {
        const pageContext = getFastSpringPageContext();
        return {
            currentPlan: pageContext.currentPlan,
            selectedPlan: pageContext.selectedPlan,
            subscriptionUserId: pageContext.userId,
            checkoutPlan: null,
            checkoutLoading: false,
            checkoutError: '',
            bodyOverflowBeforeCheckout: '',
            fastSpringDataCallback: null,
            fastSpringErrorCallback: null,
            checkoutRequestId: 0,
            plans: [
                {
                    name: 'Free',
                    title: 'Free',
                    price: '$0',
                    priceNote: 'forever',
                    features: ['One business', 'Manage clients and items', 'View and edit existing documents'],
                    productPath: ''
                },
                {
                    name: 'Pro',
                    title: 'Annual Pro',
                    price: '$29.9',
                    originalPrice: '$49.9',
                    discountLabel: 'Save 40%',
                    priceNote: 'per year',
                    features: ['Unlimited invoices and estimates', 'One business', 'Item Inventory', 'Shared access on web and mobile'],
                    productPath: FASTSPRING_PRO_PRODUCT_PATH
                },
                {
                    name: 'Premium',
                    title: 'Annual Premium',
                    price: '$49.9',
                    originalPrice: '$79.9',
                    discountLabel: 'Save 40%',
                    priceNote: 'per year',
                    features: ['Unlimited invoices and estimates', 'Multiple businesses', 'Item Inventory', 'Shared access on web and mobile'],
                    productPath: 'yearly-premium'
                }
            ]
        };
    },
    mounted() {
        document.addEventListener('keydown', this.handleKeydown);
    },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
    this.checkoutRequestId += 1;
    this.clearFastSpringCheckout();
    document.body.style.overflow = this.bodyOverflowBeforeCheckout;
        if (window.fastSpringSubscriptionDataCallback === this.fastSpringDataCallback) {
            delete window.fastSpringSubscriptionDataCallback;
        }
        if (window.fastSpringSubscriptionErrorCallback === this.fastSpringErrorCallback) {
            delete window.fastSpringSubscriptionErrorCallback;
        }
    },
    methods: {
    canSubscribe(planName) {
      return planName !== this.currentPlan;
        },
        goToAccount() {
            this.$router.push('/account');
        },
        goToInvoices() {
            this.$router.push('/invoice');
        },
        handleKeydown(event) {
            if (event.key === 'Escape' && this.checkoutPlan) {
                this.closeCheckout();
            }
        },
    closeCheckout() {
      this.checkoutRequestId += 1;
      this.clearFastSpringCheckout();
      this.checkoutLoading = false;
      this.checkoutPlan = null;
      this.checkoutError = '';
      document.body.style.overflow = this.bodyOverflowBeforeCheckout;
    },
    openCheckout(plan) {
      this.checkoutRequestId += 1;
      const requestId = this.checkoutRequestId;
      this.clearFastSpringCheckout();
      this.selectedPlan = plan.name;
      this.checkoutPlan = plan;
            this.checkoutLoading = true;
            this.checkoutError = '';
            this.bodyOverflowBeforeCheckout = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            if (this.subscriptionUserId === '') {
                this.reportSubscriptionError(new Error('subscription userId is empty'), 'open_checkout_missing_user');
                this.checkoutLoading = false;
                this.checkoutError = 'The subscription account could not be identified. Please return to Account and try again.';
                return;
            }
            this.$nextTick(() => {
                this.loadFastSpring(plan, requestId).catch((error) => {
                    if (requestId !== this.checkoutRequestId) {
                        return;
                    }
                    this.reportSubscriptionError(error, 'load_fastspring', {
                        planName: plan.name,
                        productPath: plan.productPath
                    });
                    this.checkoutLoading = false;
                    this.checkoutError = 'The secure checkout could not be loaded. Please try again.';
        });
      });
    },
    clearFastSpringCheckout() {
      if (
        window.fastspring &&
        window.fastspring.builder &&
        typeof window.fastspring.builder.reset === 'function'
      ) {
        try {
          window.fastspring.builder.reset();
        } catch (error) {
          this.reportSubscriptionError(error, 'clear_checkout');
        }
      }
      delete window.fscSession;
    },
    checkoutSession(plan) {
            return {
                reset: true,
                products: [{path: plan.productPath, quantity: 1}],
                tags: {userId: this.subscriptionUserId}
            };
        },
        loadFastSpring(plan, requestId) {
            const session = this.checkoutSession(plan);
            this.fastSpringDataCallback = () => {
                if (requestId !== this.checkoutRequestId) {
                    return;
                }
                this.checkoutLoading = false;
            };
            this.fastSpringErrorCallback = (error) => {
                if (requestId !== this.checkoutRequestId) {
                    return;
                }
                this.reportSubscriptionError(error || new Error('FastSpring checkout error callback'), 'fastspring_error_callback', {
                    planName: plan.name,
                    productPath: plan.productPath
                });
                this.checkoutLoading = false;
                this.checkoutError = 'The secure checkout could not be loaded. Please try again.';
            };
            window.fastSpringSubscriptionDataCallback = this.fastSpringDataCallback;
            window.fastSpringSubscriptionErrorCallback = this.fastSpringErrorCallback;

            if (window.fastspring && window.fastspring.builder) {
                try {
                    window.fastspring.builder.push(session);
                    return Promise.resolve();
                } catch (error) {
                    return Promise.reject(error);
                }
            }

            window.fscSession = session;
            const existingScript = document.getElementById(FASTSPRING_SCRIPT_ID);
            if (existingScript) {
                return new Promise((resolve, reject) => {
                    existingScript.addEventListener('load', resolve, {once: true});
                    existingScript.addEventListener('error', () => reject(new Error('FastSpring script failed to load')), {once: true});
                });
            }

            return new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.id = FASTSPRING_SCRIPT_ID;
                script.src = FASTSPRING_SCRIPT_URL;
                script.type = 'text/javascript';
                script.dataset.storefront = FASTSPRING_STOREFRONT;
                script.dataset.dataCallback = 'fastSpringSubscriptionDataCallback';
                script.dataset.errorCallback = 'fastSpringSubscriptionErrorCallback';
                script.onload = resolve;
                script.onerror = () => reject(new Error('FastSpring script failed to load'));
                document.head.appendChild(script);
            });
        },
        reportSubscriptionError(error, operation, context) {
            reportHandledError(error, {
                level: 'error',
                module: 'subscription',
                operation: operation,
                context: Object.assign({
                    currentPlan: this.currentPlan,
                    selectedPlan: this.selectedPlan,
                    userId: this.subscriptionUserId,
                    storefront: FASTSPRING_STOREFRONT
                }, context || {})
            });
        }
    }
};
</script>

<style scoped>
.subscription-page {
    min-height: 100vh;
    padding: 24px 24px 44px;
    background: #eef3f8;
    color: #1f2933;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, Helvetica, sans-serif;
}

.subscription-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 1180px;
    margin: 0 auto 30px;
    padding: 0 0 20px;
    border-bottom: 1px solid #dce4ee;
}

.brand,
.close-button,
.close-checkout {
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    cursor: pointer;
}

.brand {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 0;
    font-size: 21px;
    font-weight: 700;
}

.brand img {
    width: 38px;
    height: 38px;
}

.close-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: 1px solid #d7dee8;
    border-radius: 8px;
    background: #ffffff;
    color: #56616f;
    font-size: 20px;
}

.close-button:hover,
.close-button:focus {
    border-color: #aeb9c7;
    background: #eef3f8;
}

.hero {
    max-width: 1180px;
    margin: 0 auto 22px;
}

.eyebrow {
    color: #0d7df7;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: uppercase;
}

.hero h1 {
    max-width: 760px;
    margin: 0;
    color: #12213a;
    font-size: 36px;
    line-height: 1.18;
    letter-spacing: 0;
}

.plans {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0;
    max-width: 1180px;
    margin: 0 auto;
    overflow: hidden;
    border: 1px solid #d7e0ea;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 18px 44px rgba(28, 45, 68, 0.08);
}

.plan {
    position: relative;
    min-height: 500px;
    display: flex;
    flex-direction: column;
    padding: 30px;
    border: 0;
    border-right: 1px solid #dfe6ee;
    border-radius: 0;
    background: #ffffff;
    transition: background 180ms ease, color 180ms ease;
}

.plan:last-child {
    border-right: 0;
}

.plan:hover {
    background: #f8fafc;
}

.plan-featured {
    background: #12213a;
    color: #ffffff;
}

.plan-featured:hover {
    background: #172a48;
}

.plan-meta {
    min-height: 30px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.plan-badge,
.discount-badge {
    display: inline-flex;
    align-items: center;
    min-height: 28px;
    padding: 0 12px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 700;
}

.plan-badge {
    background: #ffd84d;
    color: #162238;
}

.plan-badge-placeholder {
    min-width: 1px;
}

.discount-badge {
    margin-left: auto;
    background: #e9f3ff;
    color: #0b69ce;
}

.plan-featured .discount-badge {
    background: rgba(255, 255, 255, 0.14);
    color: #ffffff;
}

.plan h2 {
    margin: 20px 0 0;
    color: #12213a;
    font-size: 27px;
    line-height: 1.2;
}

.price-block {
    min-height: 88px;
    margin-top: 18px;
}

.original-price {
    min-height: 20px;
    color: #7a8796;
    font-size: 13px;
}

.original-price span {
    text-decoration: line-through;
    text-decoration-thickness: 1px;
}

.original-price.is-empty {
    visibility: hidden;
}

.price {
    display: flex;
    align-items: flex-end;
    gap: 6px;
    margin-top: 7px;
}

.price strong {
    color: #12213a;
    font-size: 42px;
    line-height: 1;
}

.price span {
    margin-bottom: 6px;
    color: #657282;
    font-size: 15px;
}

.plan-featured h2,
.plan-featured .price strong {
    color: #ffffff;
}

.plan-featured .original-price,
.plan-featured .price span {
    color: #bac7d8;
}

.feature-list {
    margin: 24px 0 0;
    padding: 22px 0 0;
    border-top: 1px solid #e0e7ef;
    list-style: none;
}

.feature-list li {
    display: flex;
    gap: 10px;
    margin-top: 12px;
    color: #2c3a49;
    line-height: 1.45;
}

.feature-list li i {
    flex: 0 0 20px;
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-top: 1px;
    border-radius: 50%;
    background: #e8f3ff;
    color: #0d7df7;
    font-size: 12px;
}

.plan-featured .feature-list {
    border-top-color: rgba(255, 255, 255, 0.16);
}

.plan-featured .feature-list li {
    color: #edf4fc;
}

.plan-featured .feature-list li i {
    background: rgba(255, 255, 255, 0.14);
    color: #70b7ff;
}

.actions {
    min-height: 50px;
    margin-top: auto;
    padding-top: 30px;
}

.plan-button {
    width: 100%;
    min-height: 48px;
    border: 1px solid #0d7df7;
    border-radius: 8px;
    background: #0d7df7;
    color: #ffffff;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: background 160ms ease, border-color 160ms ease, box-shadow 160ms ease;
}

.plan-featured .plan-button {
    border-color: #ffffff;
    background: #ffffff;
    color: #0b69ce;
}

.plan-featured .plan-button:hover,
.plan-featured .plan-button:focus {
    border-color: #dcecff;
    background: #dcecff;
    color: #095bac;
}

.plan-button:hover,
.plan-button:focus {
    background: #0b6fe0;
    border-color: #0b6fe0;
    box-shadow: 0 8px 18px rgba(13, 125, 247, 0.18);
}

.secondary-button {
    border-color: #cbd5e1;
    background: #ffffff;
    color: #334155;
}

.secondary-button:hover,
.secondary-button:focus {
    border-color: #aeb9c7;
    background: #f8fafc;
}

.checkout-overlay {
    position: fixed;
    inset: 0;
    z-index: 4000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: rgba(13, 25, 43, 0.66);
    backdrop-filter: blur(3px);
}

.checkout-dialog {
    width: min(1060px, 100%);
    max-height: calc(100vh - 40px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid #d7e0ea;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 26px 80px rgba(9, 20, 36, 0.3);
}

.checkout-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    flex: 0 0 auto;
    min-height: 78px;
    padding: 16px 22px;
    border-bottom: 1px solid #e1e7ef;
}

.checkout-heading h2 {
    margin: 4px 0 0;
    color: #17212f;
    font-size: 22px;
}

.close-checkout {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    border: 1px solid #d7e0ea;
    border-radius: 8px;
    background: #ffffff;
    color: #526071;
    font-size: 19px;
}

.close-checkout:hover,
.close-checkout:focus {
    border-color: #aeb9c7;
    background: #eef3f8;
}

.checkout-body {
    min-height: 320px;
    padding: 22px;
    overflow: auto;
}

.checkout-frame {
    position: relative;
    min-height: 590px;
}

.checkout-frame.is-hidden {
    display: none;
}

.checkout-loading {
    position: absolute;
    inset: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: #657282;
    background: rgba(255, 255, 255, 0.96);
}

.checkout-loading i {
    color: #0d7df7;
    font-size: 22px;
}

#fsc-embedded-checkout-container {
    width: 100%;
    min-height: 590px;
}

.checkout-error {
    padding: 18px;
    border: 1px solid #f1b5b5;
    border-radius: 8px;
    background: #fff5f5;
    color: #a42828;
}

.support-note {
    max-width: 1180px;
    margin: 22px auto 0;
    color: #657282;
    font-size: 14px;
    line-height: 1.6;
    text-align: center;
}

.support-note a {
    color: #0d7df7;
    font-weight: 700;
    text-decoration: none;
}

.checkout-modal-enter-active,
.checkout-modal-leave-active {
    transition: opacity 160ms ease;
}

.checkout-modal-enter-active .checkout-dialog,
.checkout-modal-leave-active .checkout-dialog {
    transition: transform 160ms ease, opacity 160ms ease;
}

.checkout-modal-enter,
.checkout-modal-leave-to {
    opacity: 0;
}

.checkout-modal-enter .checkout-dialog,
.checkout-modal-leave-to .checkout-dialog {
    opacity: 0;
    transform: translateY(10px);
}

@media (max-width: 900px) {
    .subscription-topbar,
    .hero,
    .plans {
        max-width: 560px;
    }

    .plans {
        grid-template-columns: 1fr;
        gap: 14px;
        overflow: visible;
        border: 0;
        background: transparent;
        box-shadow: none;
    }

    .plan {
        min-height: auto;
        border: 1px solid #d7e0ea;
        border-radius: 8px;
        box-shadow: 0 12px 28px rgba(28, 45, 68, 0.06);
    }
}

@media (max-width: 600px) {
    .subscription-page {
        padding: 20px 16px 32px;
    }

    .subscription-topbar {
        margin-bottom: 28px;
    }

    .hero h1 {
        font-size: 32px;
    }

    .plan {
        padding: 24px;
    }

    .checkout-overlay {
        padding: 10px;
    }

    .checkout-dialog {
        max-height: calc(100vh - 20px);
    }

    .checkout-heading {
        min-height: 70px;
        padding: 14px 16px;
    }

    .checkout-body {
        padding: 14px;
    }

    .checkout-frame,
    #fsc-embedded-checkout-container {
        min-height: 520px;
    }
}

/* Subscription workspace redesign */
.subscription-page {
    min-height: 100vh;
    padding: 28px 28px 40px;
    background: #f3f6fa;
    color: #172033;
}

.subscription-topbar {
    max-width: 1240px;
    min-height: 56px;
    margin: 0 auto 44px;
    padding: 0 0 22px;
    border-bottom: 1px solid #d9e1eb;
}

.brand {
    gap: 12px;
    font-size: 22px;
    font-weight: 750;
    letter-spacing: 0;
}

.brand img {
    width: 42px;
    height: 42px;
    border-radius: 8px;
}

.close-button,
.close-checkout {
    border: 1px solid #d5deea;
    border-radius: 8px;
    background: #ffffff;
    color: #5f6c7c;
    transition: border-color 160ms ease, background 160ms ease, color 160ms ease;
}

.close-button:hover,
.close-button:focus-visible,
.close-checkout:hover,
.close-checkout:focus-visible {
    border-color: #92a2b6;
    background: #f8fafc;
    color: #172033;
    outline: none;
}

.subscription-intro {
    max-width: 1240px;
    margin: 0 auto 30px;
}

.subscription-intro h1 {
    max-width: 820px;
    margin: 0;
    color: #172033;
    font-size: 46px;
    font-weight: 750;
    line-height: 1.08;
    letter-spacing: 0;
}

.plan-workspace {
    display: grid;
    grid-template-columns: 340px minmax(0, 1fr);
    max-width: 1240px;
    min-height: 570px;
    margin: 0 auto;
    overflow: hidden;
    border: 1px solid #d5deea;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 22px 60px rgba(32, 49, 74, 0.09);
}

.plan-selector {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 16px;
    border-right: 1px solid #dce4ed;
    background: #edf2f7;
}

.plan-option {
    position: relative;
    flex: 1 1 0;
    min-height: 150px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    overflow: hidden;
    padding: 24px 20px 24px 24px;
    border: 1px solid transparent;
    border-radius: 6px;
    background: transparent;
    color: #445166;
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition: background 180ms ease, border-color 180ms ease, color 180ms ease, transform 180ms ease;
}

.plan-option::before {
    content: "";
    position: absolute;
    top: 20px;
    bottom: 20px;
    left: 0;
    width: 3px;
    background: transparent;
    transition: background 180ms ease;
}

.plan-option:hover {
    color: #172033;
    background: #e5ebf2;
}

.plan-option:focus-visible {
    border-color: #1677ee;
    outline: none;
}

.plan-option.is-selected {
    border-color: #d7e0eb;
    background: #ffffff;
    color: #172033;
    box-shadow: 0 8px 24px rgba(31, 50, 78, 0.08);
    transform: translateX(3px);
}

.plan-option.is-selected::before {
    background: #1677ee;
}

.plan-option-main {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.plan-option-title {
    display: block;
    overflow-wrap: anywhere;
    font-size: 19px;
    font-weight: 700;
    line-height: 1.25;
    letter-spacing: 0;
}

.plan-option-price {
    display: flex;
    align-items: baseline;
    gap: 6px;
    color: #758194;
}

.plan-option-price strong {
    color: inherit;
    font-size: 28px;
    font-weight: 700;
    line-height: 1;
}

.plan-option-price small {
    font-size: 12px;
    line-height: 1.25;
}

.plan-option.is-selected .plan-option-price strong {
    color: #1677ee;
}

.plan-option-status {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    color: #8c98a8;
}

.plan-option-status i {
    font-size: 17px;
    transition: color 180ms ease, transform 180ms ease;
}

.plan-option.is-selected .plan-option-status i {
    color: #1677ee;
    transform: translateX(4px);
}

.current-marker {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #1677ee;
    box-shadow: 0 0 0 4px #e7f2ff;
}

.plan-detail {
    min-width: 0;
    display: flex;
    flex-direction: column;
    padding: 52px 56px 46px;
    background: #ffffff;
}

.plan-detail-heading {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: start;
    gap: 36px;
    padding-bottom: 38px;
    border-bottom: 1px solid #dce4ed;
}

.plan-detail-title {
    min-width: 0;
}

.plan-detail-title h2 {
    margin: 18px 0 0;
    color: #172033;
    font-size: 40px;
    font-weight: 750;
    line-height: 1.08;
    letter-spacing: 0;
}

.status-label,
.saving-label {
    display: inline-flex;
    align-items: center;
    min-height: 28px;
    padding: 0 11px;
    border: 1px solid #b9d8fb;
    border-radius: 4px;
    background: #edf6ff;
    color: #1268cc;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0;
}

.plan-detail-price {
    min-width: 220px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    padding-top: 4px;
}

.plan-detail-price .original-price {
    min-height: 20px;
    color: #8792a2;
    font-size: 15px;
    line-height: 1.3;
    text-decoration: line-through;
    text-decoration-thickness: 1px;
}

.current-price {
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 8px;
}

.current-price strong {
    color: #172033;
    font-size: 56px;
    font-weight: 760;
    line-height: 0.92;
    letter-spacing: 0;
}

.current-price span {
    margin-bottom: 4px;
    color: #6c7888;
    font-size: 15px;
    line-height: 1.2;
    white-space: nowrap;
}

.feature-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0;
    margin: 34px 0 0;
    padding: 0;
    list-style: none;
}

.feature-grid li {
    min-height: 76px;
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 22px 18px 0;
    border-bottom: 1px solid #e2e8f0;
    color: #344155;
    font-size: 16px;
    line-height: 1.4;
}

.feature-grid li:nth-child(odd) {
    margin-right: 24px;
}

.feature-grid li i {
    flex: 0 0 26px;
    width: 26px;
    height: 26px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #eaf4ff;
    color: #1677ee;
    font-size: 13px;
}

.plan-detail-actions {
    min-height: 52px;
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    margin-top: auto;
    padding-top: 34px;
}

.plan-button {
    width: 280px;
    min-height: 52px;
    border: 1px solid #1677ee;
    border-radius: 6px;
    background: #1677ee;
    color: #ffffff;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 0;
    cursor: pointer;
    transition: background 160ms ease, border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
}

.plan-button:hover,
.plan-button:focus-visible {
    border-color: #0f68d7;
    background: #0f68d7;
    box-shadow: 0 10px 24px rgba(22, 119, 238, 0.2);
    outline: none;
    transform: translateY(-1px);
}

.secondary-button {
    border-color: #cbd5e1;
    background: #ffffff;
    color: #334155;
}

.secondary-button:hover,
.secondary-button:focus-visible {
    border-color: #9faec0;
    background: #f7f9fc;
    color: #172033;
    box-shadow: none;
}

.support-note {
    max-width: 1240px;
    margin: 20px auto 0;
    color: #6d7989;
    font-size: 14px;
    line-height: 1.5;
    text-align: right;
}

.support-note a {
    color: #1677ee;
}

.checkout-overlay {
    padding: 24px;
    background: rgba(18, 28, 43, 0.72);
    backdrop-filter: blur(6px);
}

.checkout-dialog {
    width: min(1100px, 100%);
    max-height: calc(100vh - 48px);
    border: 1px solid #cfd9e5;
    border-radius: 8px;
    box-shadow: 0 28px 90px rgba(8, 18, 34, 0.35);
}

.checkout-heading {
    min-height: 82px;
    padding: 17px 24px;
    background: #f7f9fc;
}

.checkout-heading h2 {
    margin: 3px 0 0;
    color: #172033;
    font-size: 23px;
    letter-spacing: 0;
}

.eyebrow {
    color: #1677ee;
    font-size: 12px;
    letter-spacing: 0;
}

.checkout-body {
    padding: 24px;
}

.checkout-frame,
#fsc-embedded-checkout-container {
    min-height: 590px;
}

.checkout-loading {
    flex-direction: column;
    gap: 14px;
    padding: 48px;
    background: #ffffff;
}

.checkout-loading span {
    display: block;
    width: min(680px, 86%);
    height: 72px;
    border-radius: 6px;
    background: #edf2f7;
    animation: checkout-pulse 1.1s ease-in-out infinite alternate;
}

.checkout-loading span:nth-child(2) {
    width: min(520px, 72%);
}

.checkout-loading span:nth-child(3) {
    width: min(600px, 80%);
}

.checkout-error {
    border-radius: 6px;
}

.plan-swap-enter-active,
.plan-swap-leave-active {
    transition: opacity 170ms ease, transform 170ms ease;
}

.plan-swap-enter {
    opacity: 0;
    transform: translateY(8px);
}

.plan-swap-leave-to {
    opacity: 0;
    transform: translateY(-5px);
}

@keyframes checkout-pulse {
    from {
        opacity: 0.55;
    }
    to {
        opacity: 1;
    }
}

.plan-comparison {
    display: grid;
    grid-template-columns: minmax(250px, 0.82fr) minmax(310px, 1.09fr) minmax(310px, 1.09fr);
    max-width: 1240px;
    min-height: 590px;
    margin: 0 auto;
    overflow: hidden;
    border: 1px solid #d5deea;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 22px 60px rgba(32, 49, 74, 0.09);
}

.plan-lane {
    position: relative;
    min-width: 0;
    display: flex;
    flex-direction: column;
    padding: 38px 34px 32px;
    border-right: 1px solid #dce4ed;
    background: #ffffff;
}

.plan-lane:last-child {
    border-right: 0;
}

.plan-lane::before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    height: 4px;
    background: transparent;
}

.plan-lane.is-highlighted {
    background: #f7fbff;
}

.plan-lane.is-highlighted::before,
.plan-lane.is-current::before {
    background: #1677ee;
}

.plan-lane-heading {
    min-height: 222px;
    padding-bottom: 30px;
    border-bottom: 1px solid #dce4ed;
}

.plan-lane-meta {
    min-height: 28px;
}

.plan-lane h2 {
    min-height: 66px;
    margin: 22px 0 0;
    color: #172033;
    font-size: 31px;
    font-weight: 750;
    line-height: 1.08;
    letter-spacing: 0;
}

.plan-lane-price {
    margin-top: 18px;
}

.plan-lane-price .original-price {
    display: block;
    min-height: 20px;
    color: #8792a2;
    font-size: 14px;
    line-height: 1.3;
    text-decoration: line-through;
    text-decoration-thickness: 1px;
}

.plan-lane-price .original-price.is-empty {
    visibility: hidden;
}

.plan-lane .current-price {
    justify-content: flex-start;
    margin-top: 8px;
}

.plan-lane .current-price strong {
    font-size: 46px;
}

.plan-feature-list {
    margin: 28px 0 0;
    padding: 0;
    list-style: none;
}

.plan-feature-list li {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    min-height: 48px;
    padding: 10px 0;
    color: #344155;
    font-size: 15px;
    line-height: 1.45;
}

.plan-feature-list li i {
    flex: 0 0 24px;
    width: 24px;
    height: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #eaf4ff;
    color: #1677ee;
    font-size: 12px;
}

.plan-lane-actions {
    min-height: 52px;
    display: flex;
    align-items: flex-end;
    margin-top: auto;
    padding-top: 28px;
}

.plan-lane .plan-button {
    width: 100%;
}

@media (max-width: 900px) {
    .subscription-topbar,
    .subscription-intro,
    .plan-workspace,
    .plan-comparison,
    .support-note {
        max-width: 680px;
    }

    .subscription-topbar {
        margin-bottom: 34px;
    }

    .subscription-intro h1 {
        font-size: 40px;
    }

    .plan-workspace {
        grid-template-columns: minmax(0, 1fr);
        min-height: 0;
        overflow: visible;
    }

    .plan-selector {
        flex-direction: row;
        gap: 8px;
        overflow-x: auto;
        padding: 12px;
        border-right: 0;
        border-bottom: 1px solid #dce4ed;
        scroll-snap-type: x mandatory;
        scrollbar-width: thin;
    }

    .plan-option {
        flex: 0 0 210px;
        min-height: 118px;
        padding: 20px 16px 20px 20px;
        scroll-snap-align: start;
    }

    .plan-option.is-selected {
        transform: translateY(-2px);
    }

    .plan-option::before {
        top: auto;
        right: 16px;
        bottom: 0;
        left: 16px;
        width: auto;
        height: 3px;
    }

    .plan-option-price strong {
        font-size: 25px;
    }

    .plan-detail {
        min-height: 470px;
        padding: 40px;
    }

    .plan-comparison {
        grid-template-columns: minmax(0, 1fr);
        max-width: 680px;
        min-height: 0;
        overflow: hidden;
    }

    .plan-lane {
        min-height: 540px;
        border-right: 0;
        border-bottom: 1px solid #dce4ed;
    }

    .plan-lane:last-child {
        border-bottom: 0;
    }
}

@media (max-width: 600px) {
    .subscription-page {
        padding: 18px 14px 28px;
    }

    .subscription-topbar {
        min-height: 50px;
        margin-bottom: 30px;
        padding-bottom: 18px;
    }

    .brand {
        font-size: 20px;
    }

    .brand img {
        width: 38px;
        height: 38px;
    }

    .close-button {
        width: 38px;
        height: 38px;
    }

    .subscription-intro {
        margin-bottom: 22px;
    }

    .subscription-intro h1 {
        font-size: 34px;
        line-height: 1.12;
    }

    .plan-selector {
        border-radius: 8px 8px 0 0;
    }

    .plan-option {
        flex-basis: 190px;
        min-height: 108px;
        padding: 17px 14px 17px 18px;
    }

    .plan-option-title {
        font-size: 17px;
    }

    .plan-option-main {
        gap: 12px;
    }

    .plan-option-price strong {
        font-size: 23px;
    }

    .plan-option-price small {
        display: none;
    }

    .plan-detail {
        min-height: 500px;
        padding: 30px 24px 26px;
    }

    .plan-detail-heading {
        grid-template-columns: minmax(0, 1fr);
        gap: 24px;
        padding-bottom: 28px;
    }

    .plan-detail-title h2 {
        margin-top: 15px;
        font-size: 32px;
    }

    .plan-detail-price {
        min-width: 0;
        align-items: flex-start;
        padding-top: 0;
    }

    .current-price {
        justify-content: flex-start;
    }

    .current-price strong {
        font-size: 48px;
    }

    .feature-grid {
        grid-template-columns: minmax(0, 1fr);
        margin-top: 24px;
    }

    .feature-grid li,
    .feature-grid li:nth-child(odd) {
        min-height: 62px;
        margin-right: 0;
        padding: 14px 0;
    }

    .plan-detail-actions {
        justify-content: stretch;
        padding-top: 28px;
    }

    .plan-button {
        width: 100%;
    }

    .plan-comparison {
        grid-template-columns: minmax(0, 1fr);
        min-height: 0;
    }

    .plan-lane {
        padding: 30px 24px 26px;
    }

    .plan-lane-heading {
        min-height: 210px;
    }

    .plan-lane h2 {
        min-height: 58px;
        margin-top: 18px;
        font-size: 27px;
    }

    .plan-lane .current-price strong {
        font-size: 40px;
    }

    .support-note {
        margin-top: 18px;
        text-align: left;
    }

    .checkout-overlay {
        padding: 8px;
    }

    .checkout-dialog {
        max-height: calc(100vh - 16px);
    }

    .checkout-heading {
        min-height: 70px;
        padding: 13px 14px;
    }

    .checkout-body {
        padding: 12px;
    }

    .checkout-loading {
        padding: 28px 14px;
    }

    .checkout-frame,
    #fsc-embedded-checkout-container {
        min-height: 520px;
    }
}

/* Unified plan comparison */
.subscription-page {
    min-height: 100vh;
    padding: 22px 24px 30px;
    background: #f4f7fb;
}

.subscription-topbar {
    max-width: 1210px;
    min-height: 52px;
    margin: 0 auto 26px;
    padding: 0 0 16px;
}

.brand {
    gap: 11px;
    font-size: 21px;
}

.brand img {
    width: 40px;
    height: 40px;
}

.subscription-intro {
    max-width: 1210px;
    margin: 0 auto 20px;
}

.subscription-intro h1 {
    font-size: 39px;
    line-height: 1.08;
}

.plan-comparison {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 20px;
    max-width: 1210px;
    min-height: 0;
    overflow: visible;
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
}

.plan-lane {
    min-height: 510px;
    padding: 28px 30px 26px;
    overflow: hidden;
    border: 1px solid #cfd9e6;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 14px 34px rgba(38, 57, 83, 0.07);
    opacity: 0;
    transform: translateY(12px);
    animation: plan-lane-enter 520ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
    transition: background 180ms ease;
}

.plan-lane:nth-child(2) {
    animation-delay: 70ms;
}

.plan-lane:nth-child(3) {
    animation-delay: 140ms;
}

.plan-lane:first-child {
    background: #fafbfd;
}

.plan-lane.is-current {
    background: #fafbfd;
}

.plan-lane.is-highlighted {
    background: #f2f8ff;
}

.plan-lane::before {
    height: 4px;
}

.plan-lane.is-current::before {
    background: transparent;
}

.plan-lane.is-highlighted::before {
    background: #1677ee;
}

.plan-lane-heading {
    min-height: 176px;
    padding-bottom: 24px;
    border-bottom-color: #d8e1eb;
}

.plan-lane-meta {
    min-height: 26px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
}

.plan-index {
    color: #9aa6b5;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0.14em;
}

.status-label,
.saving-label {
    min-height: 26px;
    padding: 0 10px;
    border-radius: 4px;
    font-size: 11px;
}

.status-label {
    border-color: #c9d3df;
    background: #ffffff;
    color: #566477;
}

.saving-label {
    border-color: #b9d8fb;
    background: #eaf4ff;
    color: #1268cc;
}

.plan-lane h2 {
    min-height: 36px;
    margin: 16px 0 0;
    font-size: 29px;
    line-height: 1.12;
}

.plan-lane-price {
    margin-top: 11px;
}

.plan-lane-price .original-price {
    min-height: 18px;
    font-size: 16px;
}

.plan-lane .current-price {
    gap: 8px;
    margin-top: 5px;
}

.plan-lane .current-price strong {
    font-size: 43px;
    line-height: 0.96;
}

.plan-lane .current-price span {
    margin-bottom: 4px;
    font-size: 13px;
}

.plan-feature-list {
    margin-top: 20px;
}

.plan-feature-list li {
    min-height: 40px;
    gap: 10px;
    padding: 7px 0;
    font-size: 14px;
    line-height: 1.35;
}

.plan-feature-list li i {
    flex-basis: 20px;
    width: 20px;
    height: 20px;
    margin-top: 0;
    background: #e8f3ff;
    font-size: 10px;
}

.plan-lane-actions {
    min-height: 46px;
    padding-top: 22px;
}

.plan-lane .plan-button {
    min-height: 46px;
    border-radius: 6px;
    font-size: 15px;
}

.support-note {
    max-width: 1210px;
    margin-top: 16px;
    font-size: 13px;
}

@keyframes plan-lane-enter {
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (max-width: 900px) {
    .subscription-page {
        padding: 20px 18px 30px;
    }

    .subscription-topbar,
    .subscription-intro,
    .plan-comparison,
    .support-note {
        max-width: 680px;
    }

    .subscription-topbar {
        margin-bottom: 24px;
    }

    .subscription-intro h1 {
        font-size: 36px;
    }

    .plan-comparison {
        grid-template-columns: minmax(0, 1fr);
        gap: 16px;
    }

    .plan-lane,
    .plan-lane:first-child {
        min-height: 0;
        padding: 28px 30px;
        border: 1px solid #d8e1eb;
        background: #ffffff;
    }

    .plan-lane.is-highlighted {
        background: #f2f8ff;
    }

    .plan-lane:last-child {
        border-bottom: 1px solid #d8e1eb;
    }

    .plan-lane-heading {
        min-height: 0;
    }

    .plan-feature-list {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        column-gap: 24px;
    }

    .plan-lane-actions {
        max-width: 320px;
    }
}

@media (max-width: 600px) {
    .subscription-page {
        padding: 16px 12px 24px;
    }

    .subscription-topbar {
        min-height: 46px;
        margin-bottom: 22px;
        padding-bottom: 14px;
    }

    .brand {
        font-size: 19px;
    }

    .brand img,
    .close-button {
        width: 36px;
        height: 36px;
    }

    .subscription-intro {
        margin-bottom: 16px;
    }

    .subscription-intro h1 {
        font-size: 31px;
    }

    .plan-lane,
    .plan-lane:first-child {
        padding: 24px 20px;
    }

    .plan-lane h2 {
        font-size: 26px;
    }

    .plan-lane .current-price strong {
        font-size: 39px;
    }

    .plan-feature-list {
        grid-template-columns: minmax(0, 1fr);
        column-gap: 0;
    }

    .plan-lane-actions {
        max-width: none;
    }

    .support-note {
        text-align: left;
    }
}

@media (prefers-reduced-motion: reduce) {
    .plan-lane {
        opacity: 1;
        transform: none;
        animation: none;
    }

    .plan-option,
    .plan-option::before,
    .plan-option-status i,
    .plan-button,
    .plan-swap-enter-active,
    .plan-swap-leave-active,
    .checkout-modal-enter-active,
    .checkout-modal-leave-active,
    .checkout-modal-enter-active .checkout-dialog,
    .checkout-modal-leave-active .checkout-dialog {
        transition: none;
    }

    .checkout-loading span {
        animation: none;
    }
}
</style>
